// Central registry of editable page copy ("write-ups").
// Each field maps to settings.content[key]. Pages read text via getText(settings, key)
// and fall back to `default` (the original hardcoded copy) when unset.
// To make a new piece of text editable: add a field here, then use getText() in the page.

export const CONTENT_GROUPS = [
  {
    group: 'Home — Hero Stats',
    fields: [
      { key: 'home_stat1_value', label: 'Stat 1 — value', default: '3,200+' },
      { key: 'home_stat1_label', label: 'Stat 1 — label', default: 'Active Listings' },
      { key: 'home_stat2_value', label: 'Stat 2 — value', default: '150+' },
      { key: 'home_stat2_label', label: 'Stat 2 — label', default: 'Licensed Advisors' },
      { key: 'home_stat3_value', label: 'Stat 3 — value', default: '100%' },
      { key: 'home_stat3_label', label: 'Stat 3 — label', default: 'Verified Homes' },
      { key: 'home_stat4_value', label: 'Stat 4 — value', default: '4.9 / 5' },
      { key: 'home_stat4_label', label: 'Stat 4 — label', default: 'Client Rating' },
    ],
  },
  {
    group: 'Home — Sections',
    fields: [
      { key: 'home_featured_eyebrow', label: 'Featured — eyebrow', default: 'Featured Listings' },
      { key: 'home_featured_title', label: 'Featured — title', default: 'Handpicked Properties' },
      { key: 'home_featured_subtitle', label: 'Featured — subtitle', default: 'Premium homes and investments curated by our team for serious buyers.', multiline: true },
      { key: 'home_newest_eyebrow', label: 'Newest — eyebrow', default: 'Newest Additions' },
      { key: 'home_newest_title', label: 'Newest — title', default: 'Fresh on the Market' },
      { key: 'home_devs_title', label: 'Developers — title', default: 'Featured Developers' },
      { key: 'home_devs_subtitle', label: 'Developers — subtitle', default: 'A curated roster of homebuilders shaping skylines and shorelines across the Philippines.', multiline: true },
      { key: 'home_how_eyebrow', label: 'How It Works — eyebrow', default: 'How It Works' },
      { key: 'home_how_title', label: 'How It Works — title', default: 'Your Journey to Home Ownership' },
      { key: 'home_step1_title', label: 'Step 1 — title', default: 'Search' },
      { key: 'home_step1_desc', label: 'Step 1 — text', default: 'Browse thousands of verified listings filtered by location, type, and budget.', multiline: true },
      { key: 'home_step2_title', label: 'Step 2 — title', default: 'Visit' },
      { key: 'home_step2_desc', label: 'Step 2 — text', default: 'Schedule a viewing with a licensed Terra Homes advisor at your convenience.', multiline: true },
      { key: 'home_step3_title', label: 'Step 3 — title', default: 'Move In' },
      { key: 'home_step3_desc', label: 'Step 3 — text', default: 'Close the deal with confidence — we guide you through every step.', multiline: true },
      { key: 'home_agents_eyebrow', label: 'Advisors — eyebrow', default: 'Our Team' },
      { key: 'home_agents_title', label: 'Advisors — title', default: 'Meet Our Top Advisors' },
      { key: 'home_testimonials_eyebrow', label: 'Testimonials — eyebrow', default: 'Testimonials' },
      { key: 'home_testimonials_title', label: 'Testimonials — title', default: 'What Our Clients Say' },
      { key: 'home_cta_title', label: 'Bottom CTA — title', default: 'Ready to Make a Move?' },
      { key: 'home_cta_subtitle', label: 'Bottom CTA — subtitle', default: 'Whether you are buying, selling, or investing, our team is ready to make it happen.', multiline: true },
      { key: 'home_cta_primary', label: 'Bottom CTA — primary button', default: 'Browse Properties' },
      { key: 'home_cta_secondary', label: 'Bottom CTA — secondary button', default: 'Talk to an Advisor' },
      { key: 'home_agent_cta', label: 'Hero — "Become an Advisor" button', default: 'Become an Advisor' },
    ],
  },
  {
    group: 'About Page',
    fields: [
      { key: 'about_hero_title', label: 'Hero — title', default: 'Building dreams, one home at a time.' },
      { key: 'about_hero_subtitle', label: 'Hero — subtitle', default: 'A trusted real estate brokerage serving Filipino families and investors across the Philippines.', multiline: true },
      { key: 'about_story_title', label: 'Our Story — title', default: 'Built on trust, integrity, and real expertise' },
      { key: 'about_mission', label: 'Mission — text', default: 'To deliver personalized and professional real estate services that exceed our clients’ expectations, fostering long-term relationships built on trust and integrity — and to help real estate professionals and aspirants build wealth through real estate and make their dreams a reality.', multiline: true },
      { key: 'about_vision', label: 'Vision — text', default: 'A premier group of real estate advisors dedicated to providing exceptional service and expertise in helping clients achieve their real estate goals.', multiline: true },
      { key: 'about_values_title', label: 'Values — title', default: 'What we stand for' },
    ],
  },
  {
    group: 'Contact Page',
    fields: [
      { key: 'contact_hero_title', label: 'Hero — title', default: 'Get in touch' },
      { key: 'contact_hero_subtitle', label: 'Hero — subtitle', default: 'Have a question about properties, pricing, or anything else? Our team is ready to answer all your inquiries.', multiline: true },
      { key: 'contact_form_title', label: 'Form — heading', default: 'Send us a message' },
      { key: 'contact_form_subtitle', label: 'Form — subtext', default: 'We usually respond within a few hours during business hours.', multiline: true },
      { key: 'contact_hours', label: 'Office Hours — text', default: 'Mon - Fri: 9:00 AM - 6:00 PM\nSat: 9:00 AM - 4:00 PM\nSun: By appointment', multiline: true },
    ],
  },
];

// Flat { key: default } map.
export const contentDefaults = Object.fromEntries(
  CONTENT_GROUPS.flatMap((g) => g.fields.map((f) => [f.key, f.default]))
);

// Read editable copy from settings, falling back to the built-in default.
export const getText = (settings, key) => {
  const v = settings?.content?.[key];
  return v === undefined || v === null || v === '' ? contentDefaults[key] : v;
};
